import React, { useCallback, useState, useEffect } from "react";
import style from "./styles.module.scss";
import {
  Col,
  Row,
  Button,
  Input,
  InputGroup,
  DropdownMenu,
  DropdownToggle,
  DropdownItem,
  UncontrolledDropdown,
  Spinner,
  ButtonGroup,
} from "reactstrap";
import { debounce } from "../../../utils/Helper";
import { AssetStatus } from "../types";
import LaddaButton, { EXPAND_RIGHT } from "react-ladda";
import ModalRiskMassive from "../Modals/ModalRiskMassive";
import AutomaticAssetCreator from "../AutomaticAssetCreator/AutomaticAssetCreator";

const Toolbar = ({
  onChange: rootOnChange,
  subClasses,
  filter: rootFilter,
  counters,
  Link,
  role,
  searchAsset,
  TraderCanCreateAssets,
  onBloombergSearch,
  showBloomberg,
  onStartProcessing,
  ...props
}) => {
  const [filters, setFilters] = useState(rootFilter);
  const [isRiskModalOpen, setIsRiskModalOpen] = useState(false);
  const [showBloombergModal, setShowBloombergModal] = useState(false);
  const [searchType, setSearchType] = useState("CUSIP");
  const [searchValue, setSearchValue] = useState("");
  const [isAutomaticCreatorOpen, setIsAutomaticCreatorOpen] = useState(false);

  const debounceCallback = useCallback(
    debounce((nextValues) => rootOnChange(nextValues), 500),
    [],
  );

  const handleLoadRisksClick = () => {
    setIsRiskModalOpen(true);
  };

  const handleCloseRiskModal = () => {
    setIsRiskModalOpen(false);
  };

  const handleDownloadTemplate = () => {
    props.riskTemplate().then((template) => {
      if (template && template.response && template.response.url) {
        window.open(template.response.url, "_blank");
      }
    });
  };

  const handleUploadFile = async (fileData) => {
    try {
      const result = await props.uploadRiskData(fileData);
      return result;
    } catch (error) {
      return {
        error: true,
        msgError: error.message || "An unexpected error occurred during upload",
      };
    }
  };

  const onChange = (fields) => {
    const nextValues = {
      ...filters,
      ...fields,
    };
    debounceCallback(nextValues);
    setFilters(nextValues);
  };

  const handleConfirmBloomberg = () => {
    if (onBloombergSearch && searchValue.trim()) {
      onBloombergSearch(searchType, searchValue.trim());
      setShowBloombergModal(false);
      setSearchValue("");
    }
  };

  return (
    <div className={style.toolbar}>
      <Row>
        <Col lg={2}>
          <Counter {...counters} />
        </Col>
        <Col lg={4}>
          <InputGroup>
            <div className="input-group-prepend">
              <span className="input-group-text">
                <i className="fa fa-search"></i>
              </span>
            </div>

            <Input
              type="text"
              placeholder="Search something"
              value={filters.text}
              onChange={(e) => onChange({ text: e.target.value })}
            />
          </InputGroup>
        </Col>

        <Col lg={5}>
          <div className="d-flex justify-content-between">
            {subClasses ? (
              <DropDownFilterSubClass value={filters.subAssetClass} items={subClasses} onChange={onChange} />
            ) : (
              <Spinner size="sm" className="mt-2 ml-3 mr-3" />
            )}

            <DropDownFilterStatus value={filters.status} onChange={onChange} />

            <LaddaButton
              className="ml-2 mr-2 btn btn-ladda btn-primary"
              loading={false}
              disabled={false}
              onClick={() => searchAsset()}
              data-style={EXPAND_RIGHT}
            >
              Search
            </LaddaButton>
            {role != "advisor" && (role !== "trader" || (role === "trader" && TraderCanCreateAssets)) ? (
              <UncontrolledDropdown>
                <DropdownToggle caret color="primary">
                  <i className="mr-1 fa fa-plus"></i>
                  New Asset
                </DropdownToggle>
                <DropdownMenu>
                  <DropdownItem tag={Link} to="/asset">
                    Manual
                  </DropdownItem>
                  <DropdownItem onClick={() => setIsAutomaticCreatorOpen(true)}>Automatic</DropdownItem>
                  {showBloomberg && (
                    <DropdownItem onClick={() => setShowBloombergModal(true)}>Bloomberg</DropdownItem>
                  )}
                </DropdownMenu>
              </UncontrolledDropdown>
            ) : null}

            {role != "advisor" && (role !== "trader" || (role === "trader" && TraderCanCreateAssets)) ? (
              <Button
                color="primary"
                onClick={handleLoadRisksClick}
                className={`ml-2 ${style.toolbarBtn}`}
                style={{
                  borderRadius: "10px",
                  fontWeight: "500",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  padding: "0.5rem 1.5rem",
                  minWidth: "110px",
                  transition: "box-shadow 0.2s",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <i className="fa fa-upload"></i>
                Load Risks
              </Button>
            ) : null}
          </div>
        </Col>
      </Row>
      {showBloombergModal && (
        <div className="modal d-block" tabIndex="-1" role="dialog">
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Search in Bloomberg</h5>
                <button type="button" className="close" onClick={() => setShowBloombergModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="mb-3 text-center">
                  <label className="d-block">Select search type</label>
                  <div className="d-flex justify-content-center">
                    <Button
                      outline
                      color="secondary"
                      onClick={() => setSearchType("CUSIP")}
                      className="mx-2"
                      style={
                        searchType === "CUSIP"
                          ? {
                              backgroundColor: "#ADD8E6",
                              color: "#333",
                              fontWeight: "600",
                              border: "1px solid #87CEEB",
                            }
                          : {}
                      }
                    >
                      CUSIP
                    </Button>
                    <Button
                      outline
                      color="secondary"
                      onClick={() => setSearchType("TICKER")}
                      className="mx-2"
                      style={
                        searchType === "TICKER"
                          ? {
                              backgroundColor: "#ADD8E6",
                              color: "#333",
                              fontWeight: "600",
                              border: "1px solid #87CEEB",
                            }
                          : {}
                      }
                    >
                      Ticker
                    </Button>
                    <Button
                      outline
                      color="secondary"
                      onClick={() => setSearchType("ISIN")}
                      className="mx-2"
                      style={
                        searchType === "ISIN"
                          ? {
                              backgroundColor: "#ADD8E6",
                              color: "#333",
                              fontWeight: "600",
                              border: "1px solid #87CEEB",
                            }
                          : {}
                      }
                    >
                      ISIN
                    </Button>
                    <Button
                      outline
                      color="secondary"
                      onClick={() => setSearchType("BB_GLOBAL")}
                      className="mx-2"
                      style={
                        searchType === "BB_GLOBAL"
                          ? {
                              backgroundColor: "#ADD8E6",
                              color: "#333",
                              fontWeight: "600",
                              border: "1px solid #87CEEB",
                            }
                          : {}
                      }
                    >
                      FIGI
                    </Button>
                  </div>
                </div>

                <div className="mb-3">
                  <label>Search value</label>
                  <Input
                    type="text"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder="Enter value..."
                  />
                </div>
              </div>
              <div className="modal-footer">
                <Button color="secondary" onClick={() => setShowBloombergModal(false)}>
                  Cancel
                </Button>
                <Button color="primary" onClick={handleConfirmBloomberg}>
                  Confirm
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ModalRiskMassive
        open={isRiskModalOpen}
        onClose={handleCloseRiskModal}
        onDownloadTemplate={handleDownloadTemplate}
        onUpload={handleUploadFile}
        onConfirmChanges={props.confirmRiskChanges}
      />
      <AutomaticAssetCreator
        isOpen={isAutomaticCreatorOpen}
        toggle={() => setIsAutomaticCreatorOpen(!isAutomaticCreatorOpen)}
        onStartProcessing={onStartProcessing}
      />
    </div>
  );
};

const Counter = ({ total }) => (
  <div className="d-none d-lg-block">
    <h6 className={"h6 page-title " + style.totals}>Assets: {total}</h6>
  </div>
);

const DropDownFilterSubClass = ({ value, items, onChange }) => (
  <UncontrolledDropdown style={{ minWidth: "160px" }}>
    <DropdownToggle caret color="secondary" className={style.dropdown}>
      {!value ? "All Subclasses" : items.find((e) => e.SubAssetClassId == value).SubAssetClassName}
    </DropdownToggle>
    <DropdownMenu className={style.menu}>
      <DropdownItem key={`subclasses-all`} onClick={() => onChange({ subAssetClass: 0 })}>
        {"All Subclasses"}
      </DropdownItem>
      {items.map((item) => (
        <DropdownItem
          key={`subclasses-${item.SubAssetClassId}`}
          onClick={() => onChange({ subAssetClass: item.SubAssetClassId })}
        >
          {item.SubAssetClassName}
        </DropdownItem>
      ))}
    </DropdownMenu>
  </UncontrolledDropdown>
);

const DropDownFilterStatus = ({ value, onChange }) => (
  <UncontrolledDropdown style={{ minWidth: "140px" }}>
    <DropdownToggle caret color="secondary" className={style.dropdown}>
      {value == "" ? "All Statusses" : value}
    </DropdownToggle>
    <DropdownMenu>
      <DropdownItem key={`status-all`} onClick={() => onChange({ status: "" })}>
        {"All Statusses"}
      </DropdownItem>
      {Object.values(AssetStatus).map((status) => (
        <DropdownItem key={`status-${status}`} onClick={() => onChange({ status: status })}>
          {status}
        </DropdownItem>
      ))}
    </DropdownMenu>
  </UncontrolledDropdown>
);

export default Toolbar;
